import * as R from 'ramda'
import { createSelector } from 'reselect'
import moment from 'moment'
import { DATE_PICKERS_FORMAT } from 'constants/dateFormats'
import {
  hasEnoughAdultsBirthdates,
  hasEnoughBabiesBirthdates,
  hasEnoughChildrenBirthdates,
  hasParticipantsSelectionPaxCountValid,
  hasParticipantsSelectionValid
} from 'domain/pricerParticipants'
import {
  filterPackageElementsBySelection,
  isValidPrice
} from 'domain/pricer'
import {
  getCurrentSelection,
  getPackageElements,
  getPickersBindings
} from './package'
import {
  getCapacityConf,
  getMaxPax,
  getMinPax
} from './pricerCapacity'
import { getHasVisibleDepartureCityPicker } from './pricerDepartureCity'
import {
  getAvailableDurations,
  getHasVisibleDurationPicker,
  getSelectedNbNights
} from './pricerDuration'
import {
  getHasVisibleDepartureDatePicker,
  getSelectedCheckin,
  getSelectedDepartureDate
} from './pricerDepartureDate'
import {
  getHasVisibleParticipantsPicker,
  getSelectedParticipants
} from './pricerParticipants'

export const getPackageElementsFilteredByDepartureDate = createSelector(
  getPackageElements,
  getSelectedCheckin,
  filterPackageElementsBySelection
)

export const getFirstPackageElementFilteredBySelection = createSelector(
  getPackageElementsFilteredByDepartureDate,
  R.head
)

export const getSelectedCheckout = createSelector(
  getSelectedCheckin,
  getSelectedNbNights,
  getHasVisibleDurationPicker,
  getFirstPackageElementFilteredBySelection,
  (selectedCheckin, selectedNbNights, hasVisibleDuration, selectedPackageElements) => {
    if (!selectedCheckin || (hasVisibleDuration && R.isNil(selectedNbNights))) {
      return null
    }

    return moment(selectedCheckin)
      .add(selectedPackageElements.days, 'days')
      .subtract(1, 'day')
      .format(DATE_PICKERS_FORMAT)
  }
)

const isPackageParameterValid = (shouldCompare, parameterKey) => selection => (
  shouldCompare
    ? (selection[parameterKey] !== null)
    : true
)

const isSelectedPackageElementValid = selectedPackageElement => (
  selectedPackageElement?.length > 0
    ? selectedPackageElement?.[0].isAvailable
    : true
)

export const getHaveParticipantsEnoughBirthdates = createSelector(
  getSelectedParticipants,
  getCapacityConf,
  (participantsSelection, conf) => {
    const hasEnoughtAdultsBirthdates = conf?.adults?.askBirthdays
      ? hasEnoughAdultsBirthdates(participantsSelection)
      : true
    const hasEnoughtChildrenBirthdates = conf?.children?.askBirthdays
      ? hasEnoughChildrenBirthdates(participantsSelection)
      : true
    const hasEnoughtBabiesBirthdates = conf?.babies?.askBirthdays
      ? hasEnoughBabiesBirthdates(participantsSelection)
      : true

    return hasEnoughtAdultsBirthdates && hasEnoughtChildrenBirthdates && hasEnoughtBabiesBirthdates
  }
)

export const getHasValidSelection = createSelector( // TODO fix
  getCurrentSelection,
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleDepartureDatePicker,
  getPackageElementsFilteredByDepartureDate,
  (
    selection,
    hasVisibleDepartureCity,
    hasVisibleDuration,
    hasVisibleDepartureDate,
    selectedPackageElement
  ) => (
    (selection === null || selection === undefined)
      ? false
      : R.allPass([
        isPackageParameterValid(hasVisibleDepartureCity, 'departureCity'),
        isPackageParameterValid(hasVisibleDuration, 'nights'),
        isPackageParameterValid(hasVisibleDepartureDate, 'checkin'),
        R.always(isSelectedPackageElementValid(selectedPackageElement))
      ])(selection)
  )
)

export const getCanValidate = createSelector(
  getSelectedParticipants,
  getHasValidSelection,
  getHasVisibleParticipantsPicker,
  getHaveParticipantsEnoughBirthdates,
  getMinPax,
  getMaxPax,
  (participantsSelection, hasSelectedPackageElement, isParticipantsPickerVisible, hasEnoughBirthdates, minPax, maxPax) => (
    R.allPass([
      R.always(hasSelectedPackageElement),
      isParticipantsPickerVisible ? hasParticipantsSelectionPaxCountValid(minPax, maxPax) : R.always(true),
      isParticipantsPickerVisible ? hasParticipantsSelectionValid : R.always(true),
      R.always(hasEnoughBirthdates)
    ])(participantsSelection)
  )
)

export const getPickersSelectionPrice = createSelector(
  getCurrentSelection,
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleDepartureDatePicker,
  getPickersBindings,
  getSelectedDepartureDate,
  getAvailableDurations,
  (
    selection,
    hasVisibleDepartureCity,
    hasVisibleDuration,
    hasVisibleDepartureDate,
    pickersBindings,
    selectedDepartureDate,
    allDurations
  ) => {
    if (selection === null || selection === undefined) {
      return (null)
    } else if (
      selection.departureCity !== null &&
      hasVisibleDepartureCity &&
      !hasVisibleDuration &&
      !hasVisibleDepartureDate
    ) {
      const lowerCaseDepartureCity = selection.departureCity.toLowerCase()

      return ({
        hasPrice: pickersBindings[lowerCaseDepartureCity][0].hasPrice,
        price: pickersBindings[lowerCaseDepartureCity][0].price
      })
    } else if (
      selection.nights !== null &&
      hasVisibleDuration &&
      !hasVisibleDepartureCity &&
      !hasVisibleDepartureDate
    ) {
      const duration = allDurations.find(({ nights }) => nights === selection.nights)

      return (
        (duration !== undefined)
          ? { hasPrice: duration.hasPrice, price: duration.price }
          : null
      )
    } else if (selectedDepartureDate) {
      return ({
        hasPrice: selectedDepartureDate.hasPrice,
        price: selectedDepartureDate.price
      })
    }

    return (null)
  }
)

export const getHasEstimatedPrice = createSelector(
  getHasValidSelection,
  getPickersSelectionPrice,
  (hasValidSelection, pickersSelectionPrice) => (
    hasValidSelection &&
    pickersSelectionPrice !== null &&
    /*
      The `undefined` check of the hasPrice is here for retrocompat issue
      it should be removed in the future
    */
    (pickersSelectionPrice.hasPrice || pickersSelectionPrice.hasPrice === undefined) &&
    isValidPrice(pickersSelectionPrice.price)
  )
)
